/**
 * The routine routes (slice 5 spec §10) and their query keys. `['routines']`
 * is the list (both tabs under it), `['routine', id]` one routine; the
 * `routine` and `routine_run` hub events invalidate both (`app/events.ts`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../app/api";
import { meKey } from "../../app/me";
import type { AreaRef } from "../areas/api";
import { threadsKey } from "../chats/api";

export type RoutineKind = "repeat" | "once";
export type RunStatus = "running" | "done" | "skipped" | "error";
export type SkipReason = "previous_running" | "ai_not_connected" | "missed";

type Person = { id: string; name: string; initials: string; tint: string; phone_linked: boolean };
type Agent = { id: string; slug: string; name: string; glyph: string; tint: string };

/** When a routine runs, as the API sends it: `days` 0 = Sunday, times `HH:MM`. */
export type RoutineWhen = {
  kind: RoutineKind;
  days: number[];
  time: string | null;
  /** A reminder's local date, `YYYY-MM-DD`. */
  on: string | null;
  every: number | null;
  from: string | null;
  to: string | null;
};

/** A routine a chat's routine lines name (S-525), as the thread sends it: its Made row. */
export type ThreadRoutine = RoutineWhen &
  Pick<RoutineItem, "id" | "title" | "enabled" | "paused_idle" | "next_run_at" | "ended_at">;

export type RoutineItem = RoutineWhen & {
  id: string;
  title: string;
  /** The instructions' first sentence. */
  brief: string;
  for: Person | null;
  agent: Agent;
  enabled: boolean;
  paused_idle: boolean;
  model: string | null;
  text_me: boolean;
  next_run_at: string | null;
  last_run: {
    id: string;
    status: RunStatus;
    skip_reason: SkipReason | null;
    at: string;
    snippet: string | null;
    notify: boolean;
    texted: boolean;
  } | null;
  thread_id: string;
  /** The list's: the chat a reminder was asked in. */
  thread_title?: string;
  owns_thread: boolean;
  doc_id: string | null;
  ended_at: string | null;
  seeded: boolean;
  /** Its Area's chip (slice 6d spec §4.4), null with none. Missing on an older Worker. */
  area?: AreaRef | null;
  area_id?: string | null;
};

export type RoutineWeek = { runs: number; computer_ms: number };

export type RoutineList = {
  routines: RoutineItem[];
  counts: { all: number; mine: number };
  week: RoutineWeek;
  timezone: string;
};

export type RoutineRun = {
  id: string;
  status: RunStatus;
  skip_reason: SkipReason | null;
  fail_reason: string | null;
  local_date: string;
  manual: boolean;
  scheduled_for: string;
  finished_at: string | null;
  duration_ms: number | null;
  computer_ms: number | null;
  snippet: string | null;
  turn_id: string | null;
  notify: boolean;
  texted: boolean;
  can_retry: boolean;
};

export type RoutineDetail = {
  routine: Omit<RoutineItem, "last_run" | "for" | "agent"> & {
    instructions: string;
    for: Person | null;
    agent: Agent | null;
    created_via: "signup" | "join" | "page" | "agent" | "backfill";
    changed_by: { id: string; name: string } | null;
    changed_at: string | null;
    doc: { id: string; title: string; version: number; archived: boolean } | null;
  };
  runs: RoutineRun[];
  week: RoutineWeek;
  timezone: string;
};

export type RoutineTab = "everyone" | "mine";

export const routinesKey = ["routines"] as const;
export const routineKey = (id: string) => ["routine", id] as const;

/** `GET /api/routines` for a tab, filtered to an Area's slug or `none` (slice 6d). */
function routinesPath(tab: RoutineTab, area: string | null): string {
  const q = new URLSearchParams();
  if (tab === "mine") q.set("for", "me");
  if (area) q.set("area", area);
  const query = q.toString();
  return query ? `/api/routines?${query}` : "/api/routines";
}

export function useRoutines(tab: RoutineTab, area: string | null = null) {
  return useQuery({
    queryKey: area ? [...routinesKey, tab, area] : [...routinesKey, tab],
    queryFn: () => api<RoutineList>("GET", routinesPath(tab, area)),
  });
}

export function useRoutine(id: string) {
  return useQuery({
    queryKey: routineKey(id),
    queryFn: () => api<RoutineDetail>("GET", `/api/routines/${id}`),
  });
}

/** What New routine and Edit routine send (spec §10): a schedule's fields, and the rest. */
export type RoutineInput = {
  title?: string;
  instructions?: string;
  days?: number[];
  on?: string;
  time?: string;
  every?: number;
  from?: string;
  to?: string;
  model?: string | null;
  text_me?: boolean;
  enabled?: boolean;
  /** Slice 6d (spec §4.2): its Area, or none. */
  area_id?: string | null;
};

type Saved = { routine: Omit<RoutineItem, "last_run" | "for" | "agent"> };

/** Any write: the list, the routine, the sidebar's count, and Chats (a deleted routine's thread). */
function useInvalidate() {
  const client = useQueryClient();
  return (id?: string) => {
    void client.invalidateQueries({ queryKey: routinesKey });
    if (id) void client.invalidateQueries({ queryKey: routineKey(id) });
    void client.invalidateQueries({ queryKey: meKey });
    void client.invalidateQueries({ queryKey: threadsKey });
    // Its Area's page (slice 6d).
    void client.invalidateQueries({ queryKey: ["area"] });
  };
}

export function useCreateRoutine() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: RoutineInput) => api<Saved>("POST", "/api/routines", input),
    onSuccess: ({ routine }) => invalidate(routine.id),
  });
}

/** Edit routine's Save, and the On switch. */
export function useEditRoutine(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: RoutineInput) => api<Saved>("PATCH", `/api/routines/${id}`, input),
    onSuccess: () => invalidate(id),
  });
}

/** The On switch on a list row, which names its routine. */
export function useToggleRoutine() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api<Saved>("PATCH", `/api/routines/${id}`, { enabled }),
    onSettled: (_data, _error, { id }) => invalidate(id),
  });
}

export function useDeleteRoutine(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api("DELETE", `/api/routines/${id}`),
    onSuccess: () => invalidate(),
  });
}

/** Run now (spec §4.6). */
export function useRunNow(id: string) {
  const client = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => api<{ run: RoutineRun }>("POST", `/api/routines/${id}/run`),
    onSuccess: () => invalidate(id),
    onError: (error) => {
      // `409 ai_not_connected`: the connection changed since `/api/me` was read.
      if ("code" in error && error.code === "ai_not_connected") {
        void client.invalidateQueries({ queryKey: meKey });
      }
    },
  });
}
